"use client";

import { useMemo } from "react";
import { useModal } from "@/hooks/useModal";
import { useShortUrl } from "./useShortUrl";

/** The modal id, so the toolbar button and the modal agree on which one. */
export const SHARE_MODAL_ID = "share";

export function useShareModal() {
  return useModal(SHARE_MODAL_ID);
}

function embedFor(url: string): string {
  return (
    `<iframe width="100%" height="100%" src="${url}" ` +
    `style="display:block;height:100vh;width:100vw" frameborder="0" ` +
    `allow="geolocation; fullscreen" allowfullscreen></iframe>`
  );
}

/**
 * The URL to share, and the embed snippet built from it.
 *
 * The base is the *current* page URL, which carries the camera, the selected POI
 * and the active view — so a shared link reproduces what the sender was looking
 * at. It's captured when the dialog opens (keyed on `enabled`) rather than once,
 * since the camera parameters change as the user moves around; `window` is only
 * touched on the client, where the dialog can open.
 *
 * That URL is then run through the backend's short-link service (as the legacy
 * app did with its `createShortenedURL` mutation), and the short link is what we
 * share and embed once it arrives. Until then — or if shortening fails — we fall
 * back to the full URL, which shares perfectly well: shortening is a nicety, not
 * a prerequisite. `isShortening` lets the dialog show that swap is pending.
 */
export function useShareUrl(enabled: boolean) {
  const pageUrl = useMemo(
    () => (enabled && typeof window !== "undefined" ? window.location.href : ""),
    [enabled]
  );

  const { shortUrl, isPending } = useShortUrl(pageUrl, enabled);
  const url = shortUrl ?? pageUrl;

  return {
    url,
    embedCode: url ? embedFor(url) : "",
    isShortening: isPending,
  };
}
